/** The map chrome: a top bar 20px from the left with the search, the owner filter and the layer
 * pills, the tools under the search, and the detail cards under those. Every height here is fixed,
 * so the card and panel offsets are classes, not measurements. */

/** Search input width — shrinks on a narrow viewport. */
export const MAP_SEARCH_WIDTH_CLASS = 'w-[min(440px,calc(100vw-8rem))] min-w-[260px]';

/** A pill with some of its choices off: the same ring on the owner filter and the layer pills. */
export const MAP_PILL_NARROWED_CLASS = 'border-brand ring-2 ring-brand/10';

/** Owner filter and layer pills share one control height and chrome. */
export const MAP_PILL_CLASS =
  'flex h-14 items-center gap-1 rounded-[10px] border border-border bg-background px-2 text-sm shadow-modal transition-colors hover:bg-accent';

/**
 * Width of the workspace menu, the layer panels and the deal, place and location cards.
 * `max-w` overrides a dialog's `max-w-lg`. Tailwind needs the literal.
 */
export const MAP_LAYER_PANEL_WIDTH_CLASS = 'w-[296px] max-w-[296px]';

/** The bar's left inset (20px) plus its right inset (16px): what the map's width loses to it. */
export const MAP_TOP_BAR_INSET_PX = 36;

/** Set on the chrome by the top bar while the pills sit on their own line under the search. */
export const MAP_TOP_BAR_COMPACT_ATTR = 'data-top-bar-compact';

/** 22 top + 56 search + 8 + 34 tools + 12. Compact: 22 + 56 + 8 + 56 pills + 12. */
const MAP_UNDER_BAR_TOP_CLASS = 'top-[132px] [[data-top-bar-compact]_&]:top-[154px]';

/** Deal, location and place cards: under the search column, as tall as the viewport allows.
 *  This box does not scroll, a scrollport would clip the metrics panel sticking out of the right
 *  edge. Scroll {@link MAP_INFO_CARD_BODY_CLASS}. */
export const MAP_INFO_CARD_CLASS = `absolute left-5 z-40 flex flex-col max-h-[calc(100dvh-3.5rem-132px-16px)] [[data-top-bar-compact]_&]:max-h-[calc(100dvh-3.5rem-154px-16px)] ${MAP_UNDER_BAR_TOP_CLASS}`;

/** A hint beside an open card: the 20px inset, the 296px card, a 12px gap. */
export const MAP_BESIDE_CARD_LEFT_CLASS = 'left-[328px]';

/** Scrolls a tall card vertically. The horizontal axis is clipped so a negative margin never scrolls. */
export const MAP_INFO_CARD_BODY_CLASS = 'min-h-0 overflow-x-hidden overflow-y-auto';

/** New location and Driving distance open under the pills, 20px from the right. */
export const MAP_TOOLBOX_PANEL_CLASS = `absolute right-5 z-30 ${MAP_UNDER_BAR_TOP_CLASS}`;

/** Map width below which the preferences modal sits under the bar instead of centred. */
export const MAP_PANEL_CROWDED_BELOW_PX = 1120;

/** Viewport-fixed. The map chrome grows with a tall card, so `absolute` + `top: 50%` centres in that box instead of the window. */
export const MAP_PREFERENCES_MODAL_CLASS =
  'max-w-lg overflow-y-auto max-h-[min(90dvh,calc(100dvh-3.5rem-2rem))]';

/** Under the search row once the map is narrower than `MAP_PANEL_CROWDED_BELOW_PX`.
 *  `3.5rem` is the navbar; the old pin was 128px below the chrome, which starts under it. */
export const MAP_PREFERENCES_CROWDED_CLASS =
  '!top-[calc(3.5rem+128px)] !translate-y-0 !max-h-[calc(100dvh-3.5rem-128px-1.5rem)]';

/** Half of the overlay sidebar (`20rem` in `sidebar.tsx`). Unset `--dialog-anchor-x` stays at 50%. */
export const DIALOG_ANCHOR_X_EXPANDED = 'calc(50% + 10rem)';
export const DIALOG_ANCHOR_X_COLLAPSED = '50%';

/** `--sidebar-width` (20rem). Chrome only shifts when the leftover width still fits the search row. */
const MAP_SIDEBAR_WIDTH_PX = 320;
const MAP_CHROME_SHIFT_MIN_PX = MAP_SIDEBAR_WIDTH_PX + 440 + 8 + 112 + 48;

export function mapChromeShiftsForSidebar(containerWidth: number): boolean {
  return containerWidth >= MAP_CHROME_SHIFT_MIN_PX;
}
